import React, { useState, useEffect, useRef } from 'react';
import GradientIcon from '../common/GradientIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { saveGuestResult, hasClaimableGuestResult } from '../../utils/guestSession';
import GuestSignupPrompt from './GuestSignupPrompt';
import PointsEarnedRow from './PointsEarnedRow';
import confetti from 'canvas-confetti';

// 🟢 [访客模式] guest 默认 false —— 已有的 /games/word-search 路由行为完全不变
const WordSearchGame = ({ guest = false }) => {
  const navigate = useNavigate();
  const { user, reloadUser } = useAuth();

  // --- 游戏状态 ---
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(180);
  const [grid, setGrid] = useState([]);
  const [gridSize, setGridSize] = useState(10);

  // --- 单词数据 ---
  const [vocabularyPool, setVocabularyPool] = useState([]);

  // 🟢 [修复] 结算画面此前显示的是客户端原始分（"Points Earned: {score}"），
  // 而后端 routes/games.js 会把积分 clamp 到 MAX_GAME_POINTS=500，还可能加每日任务奖励，
  // 并把真实入账值放在响应的 pointsEarned 里 —— 三个游戏都拿到了却丢掉了。
  // 现在显示后端返回的真实值：null = 还没结算/没入账，不谎报数字。
  const [pointsEarned, setPointsEarned] = useState(null);

  const [wordsToFind, setWordsToFind] = useState([]);
  const [currentWordDefinition, setCurrentWordDefinition] = useState('');

  // --- 交互状态 ---
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [currentDragPath, setCurrentDragPath] = useState([]);

  // --- 反馈状态 ---
  const [foundWords, setFoundWords] = useState([]);
  const [foundCells, setFoundCells] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  const gridRef = useRef(null);

  // 🟢 [修复] 用 ref 保存最新分数，避免 endGame 从过期闭包读取，漏算最后一秒的得分
  const scoreRef = useRef(0);
  const endGameCalledRef = useRef(false);
  useEffect(() => { scoreRef.current = score; }, [score]);

  // 1. 初始化
  useEffect(() => {
    const fetchWords = async () => {
      try {
        const res = guest
          ? await apiService.getPublicRandomVocabulary('all', 100)   // 🟢 公开、缓存、封顶
          : await apiService.getRandomVocabulary('all', 100);
        if (res.vocabulary && res.vocabulary.length > 0) {
          setVocabularyPool(res.vocabulary);
        }
      } catch (error) {
        console.error("Failed to load game vocabulary:", error);
      }
    };
    fetchWords();
  }, [guest]);

  const getRandomLetter = () => String.fromCharCode(65 + Math.floor(Math.random() * 26));

  // 2. 创建网格
  const createGrid = () => {
    const sourceList = vocabularyPool.length > 0 ? vocabularyPool : [];
    if (sourceList.length === 0) return;

    const filteredWords = sourceList.filter(word => word.word.length <= 10);
    if (filteredWords.length === 0) return;

    const selectedWord = filteredWords[Math.floor(Math.random() * filteredWords.length)];
    const wordToFind = selectedWord.word.toUpperCase();

    setWordsToFind([wordToFind]);
    setFoundWords([]);
    setCurrentWordDefinition(selectedWord.definition);
    setFoundCells([]);
    setFeedback('');
    setShowFeedback(false);
    setCurrentDragPath([]);

    const newSize = Math.max(10, wordToFind.length + 2);
    setGridSize(newSize);

    const newGrid = Array(newSize).fill().map(() =>
      Array(newSize).fill().map(() => ({
        letter: '',
        isPartOfWord: false,
        wordIndex: -1,
        isFirstLetter: false
      }))
    );

    let placed = false;
    let attempts = 0;
    while (!placed && attempts < 100) {
      const direction = Math.floor(Math.random() * 8);
      const startRow = Math.floor(Math.random() * newSize);
      const startCol = Math.floor(Math.random() * newSize);
      if (canPlaceWord(newGrid, wordToFind, startRow, startCol, direction, newSize)) {
        placeWord(newGrid, wordToFind, startRow, startCol, direction, 0);
        placed = true;
      }
      attempts++;
    }

    for (let row = 0; row < newSize; row++) {
      for (let col = 0; col < newSize; col++) {
        if (newGrid[row][col].letter === '') {
          newGrid[row][col].letter = getRandomLetter();
        }
      }
    }
    setGrid(newGrid);
  };

  const canPlaceWord = (grid, word, startRow, startCol, direction, size) => {
    const directions = [[-1, -1], [-1, 0], [-1, 1], [0, -1], [0, 1], [1, -1], [1, 0], [1, 1]];
    const [dRow, dCol] = directions[direction];
    for (let i = 0; i < word.length; i++) {
      const row = startRow + i * dRow;
      const col = startCol + i * dCol;
      if (row < 0 || row >= size || col < 0 || col >= size) return false;
      if (grid[row][col].letter !== '' && grid[row][col].letter !== word[i]) return false;
    }
    return true;
  };

  const placeWord = (grid, word, startRow, startCol, direction, wordIndex) => {
    const directions = [[-1, -1], [-1, 0], [-1, 1], [0, -1], [0, 1], [1, -1], [1, 0], [1, 1]];
    const [dRow, dCol] = directions[direction];
    for (let i = 0; i < word.length; i++) {
      const row = startRow + i * dRow;
      const col = startCol + i * dCol;
      grid[row][col].letter = word[i];
      grid[row][col].isPartOfWord = true;
      grid[row][col].wordIndex = wordIndex;
      if (i === 0) grid[row][col].isFirstLetter = true;
    }
  };

  const startGame = () => {
    if (vocabularyPool.length === 0) return;
    endGameCalledRef.current = false; // 🟢 解锁本局结算
    setGameState('playing');
    setScore(0);
    setPointsEarned(null);   // 🟢 新的一局，清掉上一局的入账结果
    setTimeLeft(180);
    createGrid();
  };

  const calculatePath = (start, end) => {
    const path = [];
    const dr = end.row - start.row;
    const dc = end.col - start.col;
    const steps = Math.max(Math.abs(dr), Math.abs(dc));
    const isStraight = (dr === 0 || dc === 0 || Math.abs(dr) === Math.abs(dc));
    if (!isStraight) return [start];

    const rStep = dr === 0 ? 0 : dr / steps;
    const cStep = dc === 0 ? 0 : dc / steps;

    for (let i = 0; i <= steps; i++) {
      path.push({ row: start.row + i * rStep, col: start.col + i * cStep });
    }
    return path;
  };

  // --- 事件处理 ---

  const handleMouseDown = (row, col) => {
    setIsDragging(true);
    setDragStart({ row, col });
    setCurrentDragPath([{ row, col }]);
  };

  const handleMouseEnter = (row, col) => {
    if (!isDragging || !dragStart) return;
    const path = calculatePath(dragStart, { row, col });
    setCurrentDragPath(path);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    setDragStart(null);

    if (currentDragPath.length > 0) {
      const selectedWord = currentDragPath.map(p => grid[p.row][p.col].letter).join('');
      const reversedWord = selectedWord.split('').reverse().join('');
      const targetWord = wordsToFind[0];

      if (selectedWord === targetWord || reversedWord === targetWord) {
        handleWordFound(targetWord, currentDragPath);
      }
    }
    setCurrentDragPath([]);
  };

  // 🟢 [核心修改] 移动端滑动逻辑
  const handleTouchMove = (e) => {
    // 阻止页面滚动
    if (e.cancelable) e.preventDefault();

    // 获取触摸坐标对应的元素
    const touch = e.touches[0];
    const target = document.elementFromPoint(touch.clientX, touch.clientY);

    // 如果手指在格子上，触发“进入”逻辑
    if (target && target.dataset.row !== undefined && target.dataset.col !== undefined) {
      const row = parseInt(target.dataset.row, 10);
      const col = parseInt(target.dataset.col, 10);

      // 只有位置变了才更新，优化性能
      const lastPoint = currentDragPath[currentDragPath.length - 1];
      if (!isDragging) {
        // 如果是滑动进入的（意外情况），也可以补救触发Start，但通常TouchStart已处理
      } else if (!lastPoint || lastPoint.row !== row || lastPoint.col !== col) {
        handleMouseEnter(row, col);
      }
    }
  };

  const handleWordFound = (word, pathCells) => {
    setFoundWords(prev => [...prev, word]);
    setFoundCells(prev => {
      const newCells = [...prev];
      pathCells.forEach(pc => {
        if (!newCells.some(nc => nc.row === pc.row && nc.col === pc.col)) {
          newCells.push(pc);
        }
      });
      return newCells;
    });

    const points = word.length * 10;
    setScore(prev => prev + points);
    setFeedback(`Found: ${word}! (+${points})`);
    setShowFeedback(true);
    setTimeout(() => setShowFeedback(false), 1500);
    if (typeof confetti === 'function') confetti({ particleCount: 50, spread: 60, origin: { y: 0.6 } });
    setTimeout(() => { createGrid(); }, 1000);
  };

  const endGame = async () => {
    // 🟢 幂等：本局已结算过就返回，避免重复 saveGameScore
    if (endGameCalledRef.current) return;
    endGameCalledRef.current = true;

    setGameState('gameOver');
    const finalScore = scoreRef.current; // 🟢 从 ref 读取真实分数

    // 🟢 [访客模式] 不调用任何需要 Token 的接口，只把这一局留在本 tab
    if (guest) {
      saveGuestResult({
        gameType: 'word-search',
        score: finalScore,
        accuracy: 100,
        // 只用 ref 里的分数；foundWords 是 state，在 endGame 的闭包里可能是旧值
        wordsCorrect: 0,
        wordsTotal: 0,
        timeSpent: 180,
        displayScore: finalScore,
        playedAt: Date.now()
      });
      return;
    }

    try {
      if (finalScore > 0) {
        const saveRes = await apiService.saveGameScore({
          gameType: 'word-search',
          score: finalScore,
          accuracy: 100,
          duration: 180
        });
        // 🟢 [修复] 用后端真实入账的积分，而不是客户端原始分
        setPointsEarned(saveRes?.pointsEarned ?? null);
        await reloadUser();
        // 🟢 [修复] 用后端事务返回的累计场次触发 'game_played' 徽章
        const totalGames = saveRes?.totalGamesPlayed || 0;
        if (totalGames > 0) await apiService.checkBadges('game_played', totalGames);
      }
    } catch (error) {
      console.error("Failed to save game score:", error);
      // 🟢 [修复] 只在还没拿到入账结果时才标记失败 —— 这个 catch 也会捕获
      // checkBadges 的报错，那时积分已经入账，不能把真实数字覆盖掉
      setPointsEarned(prev => (typeof prev === 'number' ? prev : 'failed'));
    }
  };

  // 🟢 [修复] 单个计时器只依赖 gameState，函数式更新，不再每秒重建 interval
  useEffect(() => {
    if (gameState !== 'playing') return;
    const timer = setInterval(() => {
      setTimeLeft(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [gameState]);

  // 时间归零 → 结束 (在更新函数外触发，配合幂等锁只结算一次)
  useEffect(() => {
    if (gameState === 'playing' && timeLeft <= 0) {
      endGame();
    }
  }, [timeLeft, gameState]);

  // 🟢 移动端强制防止滚动 (Double Check)
  useEffect(() => {
    const preventDefault = (e) => e.preventDefault();
    const el = gridRef.current;
    // 使用 passive: false 允许 preventDefault
    if (el) el.addEventListener('touchmove', preventDefault, { passive: false });
    return () => el && el.removeEventListener('touchmove', preventDefault);
  }, [gameState]);

  // 渲染函数...
  const renderMenu = () => (
    <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-2xl mx-auto">
      <h1 className="text-4xl sm:text-6xl font-bold text-gray-800 mb-8 font-kid">🔍 WORD SEARCH</h1>
      <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8 mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Play:</h2>
        <p className="text-gray-800">Drag to select the word matching the definition.</p>
      </div>
      <motion.button
        onClick={startGame}
        disabled={vocabularyPool.length === 0}
        className={`font-bold py-4 px-8 rounded-full text-xl shadow-lg ${vocabularyPool.length === 0 ? 'bg-gray-300' : 'bg-white text-purple-600'}`}
      >
        {vocabularyPool.length === 0 ? 'Loading...' : 'Start Searching!'}
      </motion.button>
    </motion.div>
  );

  const renderGame = () => (
    <div className="max-w-4xl mx-auto"
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      // 🟢 容器层也加上 TouchEnd，以防手指划出格子区域松开
      onTouchEnd={handleMouseUp}
    >
      <div className="flex justify-between items-center mb-4 text-white font-bold text-xl px-4">
        <div>Score: {score}</div>
        <div className={`${timeLeft < 30 ? 'text-red-500 animate-pulse' : ''}`}>
          Time: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
        </div>
      </div>

      <div className="bg-white rounded-3xl p-6 shadow-2xl border-4 border-pink-300">
        <div className="bg-purple-100 rounded-xl p-4 mb-6 text-center border-2 border-purple-200">
          <h3 className="text-gray-500 text-sm uppercase font-bold mb-1">Definition</h3>
          <div className="text-xl text-purple-800 font-bold">{currentWordDefinition}</div>
          <div className="text-sm text-purple-600 mt-2">💡 Hint: the word starts on the <span className="bg-yellow-200 px-1 rounded font-bold">highlighted</span> letter!</div>
        </div>

        <div className="flex justify-center">
          <div
            ref={gridRef}
            className="grid gap-1 select-none bg-gray-50 p-4 rounded-xl border-2 border-gray-200"
            style={{
              gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
              touchAction: 'none' // 🟢 CSS 禁止浏览器手势
            }}
            // 🟢 绑定容器级 TouchMove
            onTouchMove={handleTouchMove}
          >
            {grid.map((row, rowIndex) => row.map((cell, colIndex) => {
              const isFound = foundCells.some(fc => fc.row === rowIndex && fc.col === colIndex);
              const isDraggingCell = currentDragPath.some(dc => dc.row === rowIndex && dc.col === colIndex);
              // 🟢 [降低难度] 一开始就高亮目标单词的首字母作为提示
              const isHintCell = cell.isFirstLetter;

              let cellClass = "bg-white text-gray-800";
              if (isFound) cellClass = "bg-green-500 text-white";
              else if (isDraggingCell) cellClass = "bg-purple-400 text-white scale-110 shadow-lg z-10";
              else if (isHintCell) cellClass = "bg-yellow-200 text-gray-800 ring-2 ring-yellow-400 animate-pulse";

              return (
                <div
                  key={`${rowIndex}-${colIndex}`}

                  // 🟢 数据属性 (必须)
                  data-row={rowIndex}
                  data-col={colIndex}

                  onMouseDown={() => handleMouseDown(rowIndex, colIndex)}
                  onMouseEnter={() => handleMouseEnter(rowIndex, colIndex)}

                  // 🟢 触摸开始
                  onTouchStart={(e) => {
                    handleMouseDown(rowIndex, colIndex);
                  }}

                  className={`
                            w-8 h-8 md:w-10 md:h-10 flex items-center justify-center 
                            text-lg font-bold rounded-md cursor-pointer transition-colors duration-150 border border-gray-100
                            ${cellClass}
                        `}
                >
                  {cell.letter}
                </div>
              );
            }))}
          </div>
        </div>

        <AnimatePresence>
          {showFeedback && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-6 py-3 rounded-xl shadow-xl z-20 border-2 border-green-400 text-green-600 font-bold text-xl">
              {feedback}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );

  const renderGameOver = () => (
    <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="text-center max-w-md mx-auto bg-white p-8 rounded-3xl shadow-2xl">
      <h1 className="text-4xl font-bold text-gray-800 mb-4">Time's Up!</h1>
      <div className="text-6xl mb-6">⏰</div>
      <div className="text-2xl font-bold text-purple-600 mb-2">Final Score: {score}</div>
      {/* 🟢 访客不显示积分行 —— 由注册弹窗负责说明 */}
      <div className="mb-8">
        {!guest && <PointsEarnedRow score={score} pointsEarned={pointsEarned} />}
      </div>
      <div className="flex gap-4 justify-center">
        <button onClick={startGame} className="bg-green-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-green-600">Play Again</button>
        <button onClick={() => navigate(guest ? '/try' : '/games')} className="bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-bold hover:bg-gray-300">{guest ? 'More Games' : 'Exit'}</button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-6 flex items-center justify-center">
      <AnimatePresence mode="wait">
        {gameState === 'menu' && renderMenu()}
        {gameState === 'playing' && renderGame()}
        {gameState === 'gameOver' && renderGameOver()}
      </AnimatePresence>

      {/* 🟢 [访客模式] 结算后叠加快速注册引导；
          F5 之后游戏回到菜单页，但成绩还在 sessionStorage 里 ——
          这时以细横幅形态出现，保证「随时可以领」不是空话。
          登录用户完全不渲染。 */}
      {guest && (gameState === 'gameOver' || (gameState === 'menu' && hasClaimableGuestResult())) && (
        <GuestSignupPrompt onPlayAgain={startGame} bannerOnly={gameState !== 'gameOver'} />
      )}
    </div>
  );
};

export default WordSearchGame;